<template>
  <div class="min-h-screen bg-gray-50">
    <!-- Header -->
    <div class="bg-white shadow">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div class="flex items-center justify-between">
          <div class="flex items-center">
            <i class="fas fa-images text-purple-600 text-3xl mr-3"></i>
            <div>
              <h1 class="text-2xl font-bold text-gray-900">Import de photos — Dernières visites {{ targetYear }}</h1>
              <p class="text-sm text-gray-500 mt-1">
                Récupération des photos depuis le serveur distant via SFTP
              </p>
            </div>
          </div>
          <div class="flex items-center gap-3">
            <!-- Sélecteur d'année -->
            <select
              v-model="selectedYear"
              @change="loadData"
              class="text-sm border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <option v-for="y in availableYears" :key="y" :value="y">{{ y }}</option>
            </select>

            <button
              @click="loadData"
              :disabled="loading"
              class="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <i :class="loading ? 'fas fa-spinner fa-spin' : 'fas fa-sync-alt'" class="mr-2"></i>
              Actualiser
            </button>

            <button
              @click="importAll"
              :disabled="importing || loading || totalMissing === 0"
              class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <i :class="importing ? 'fas fa-spinner fa-spin' : 'fas fa-cloud-download-alt'" class="mr-2"></i>
              <span v-if="importing">Import en cours…</span>
              <span v-else-if="totalMissing > 0">Tout récupérer ({{ totalMissing }} manquantes)</span>
              <span v-else>Tout à jour</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Main content -->
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

      <!-- Stats globales -->
      <div v-if="!loading && visits.length > 0" class="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
        <div class="bg-white rounded-lg shadow p-4 text-center">
          <div class="text-3xl font-bold text-gray-900">{{ visits.length }}</div>
          <div class="text-sm text-gray-500 mt-1">Lots avec visite N-1</div>
        </div>
        <div class="bg-white rounded-lg shadow p-4 text-center">
          <div class="text-3xl font-bold text-gray-900">{{ totalPhotos }}</div>
          <div class="text-sm text-gray-500 mt-1">Photos au total</div>
        </div>
        <div class="bg-white rounded-lg shadow p-4 text-center">
          <div class="text-3xl font-bold text-green-600">{{ totalPresent }}</div>
          <div class="text-sm text-gray-500 mt-1">Photos présentes</div>
        </div>
        <div class="bg-white rounded-lg shadow p-4 text-center">
          <div class="text-3xl font-bold" :class="totalMissing > 0 ? 'text-red-600' : 'text-green-600'">{{ totalMissing }}</div>
          <div class="text-sm text-gray-500 mt-1">Photos manquantes</div>
        </div>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="text-center py-20">
        <i class="fas fa-spinner fa-spin text-purple-600 text-5xl"></i>
        <p class="mt-4 text-gray-600">Chargement des visites de {{ targetYear }}…</p>
      </div>

      <!-- Erreur -->
      <div v-else-if="error" class="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
        <div class="flex">
          <i class="fas fa-exclamation-circle text-red-400 text-xl flex-shrink-0"></i>
          <div class="ml-3">
            <h3 class="text-sm font-medium text-red-800">Erreur de chargement</h3>
            <p class="mt-1 text-sm text-red-700">{{ error }}</p>
          </div>
        </div>
      </div>

      <!-- Aucun résultat -->
      <div v-else-if="!loading && visits.length === 0" class="text-center py-20 text-gray-400">
        <i class="fas fa-folder-open text-5xl mb-4"></i>
        <p class="text-lg">Aucune visite trouvée pour l'année {{ targetYear }}</p>
      </div>

      <!-- Table des visites -->
      <div v-else class="bg-white shadow rounded-lg overflow-hidden">
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Lot</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Adresse</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date visite</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Gestionnaire</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Photos</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
              <th class="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr
              v-for="item in visits"
              :key="item.visitId"
              class="hover:bg-gray-50 transition-colors"
            >
              <!-- Lot -->
              <td class="px-4 py-3">
                <div class="text-sm font-medium text-gray-900">{{ item.property.nsil || item.property.propertyCode || '—' }}</div>
                <div v-if="item.property.sygal" class="text-xs text-gray-400">{{ item.property.sygal }}</div>
              </td>

              <!-- Adresse -->
              <td class="px-4 py-3">
                <div class="text-sm text-gray-700">{{ item.property.addressStreet || '—' }}</div>
                <div v-if="item.property.addressZipCode || item.property.addressCity" class="text-xs text-gray-400">
                  {{ [item.property.addressZipCode, item.property.addressCity].filter(Boolean).join(' ') }}
                </div>
              </td>

              <!-- Date visite -->
              <td class="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                {{ formatDate(item.visitDate) }}
              </td>

              <!-- Gestionnaire -->
              <td class="px-4 py-3 text-sm text-gray-700">
                {{ item.technicalManager?.fullName || '—' }}
              </td>

              <!-- Photos -->
              <td class="px-4 py-3">
                <div v-if="item.photoStats.total === 0" class="text-xs text-gray-400 italic">Aucune photo</div>
                <div v-else>
                  <div class="flex items-center gap-2 mb-1">
                    <span class="text-sm font-medium text-gray-700">{{ item.photoStats.present }}/{{ item.photoStats.total }}</span>
                    <span class="text-xs text-gray-400">présentes</span>
                  </div>
                  <!-- Barre de progression -->
                  <div class="w-32 h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      class="h-2 rounded-full transition-all"
                      :class="item.photoStats.missing === 0 ? 'bg-green-500' : 'bg-yellow-400'"
                      :style="{ width: progressWidth(item.photoStats) + '%' }"
                    ></div>
                  </div>
                </div>
              </td>

              <!-- Statut -->
              <td class="px-4 py-3">
                <span
                  v-if="importResults[item.visitId]"
                  class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                  :class="importResults[item.visitId].success ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'"
                >
                  <i :class="importResults[item.visitId].success ? 'fas fa-check-circle' : 'fas fa-times-circle'" class="mr-1"></i>
                  {{ importResults[item.visitId].message }}
                </span>
                <span v-else-if="item.photoStats.total === 0" class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                  <i class="fas fa-minus mr-1"></i>Sans photo
                </span>
                <span v-else-if="item.photoStats.missing === 0" class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  <i class="fas fa-check-circle mr-1"></i>Complet
                </span>
                <span v-else class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                  <i class="fas fa-exclamation-triangle mr-1"></i>{{ item.photoStats.missing }} manquante(s)
                </span>
              </td>

              <!-- Actions -->
              <td class="px-4 py-3 text-right">
                <div class="flex items-center justify-end gap-2">
                  <!-- Voir les photos -->
                  <button
                    v-if="item.photoStats.total > 0"
                    @click="openDetail(item)"
                    class="inline-flex items-center px-2 py-1 text-xs border border-gray-300 rounded text-gray-600 hover:bg-gray-50"
                  >
                    <i class="fas fa-eye mr-1"></i>Détail
                  </button>
                  <!-- Récupérer -->
                  <button
                    v-if="item.photoStats.missing > 0"
                    @click="importVisit(item)"
                    :disabled="importingVisit[item.visitId]"
                    class="inline-flex items-center px-2 py-1 text-xs border border-transparent rounded text-white bg-purple-600 hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <i :class="importingVisit[item.visitId] ? 'fas fa-spinner fa-spin' : 'fas fa-cloud-download-alt'" class="mr-1"></i>
                    <span v-if="importingVisit[item.visitId]">…</span>
                    <span v-else>Récupérer</span>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Logs -->
      <div v-if="logs.length > 0" class="mt-8 bg-white rounded-lg shadow">
        <div class="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 class="text-lg font-semibold text-gray-900">Journal des opérations</h2>
          <button @click="logs = []" class="text-sm text-gray-500 hover:text-gray-700">Effacer</button>
        </div>
        <div class="px-6 py-4 max-h-72 overflow-y-auto space-y-1">
          <div v-for="(log, i) in logs" :key="i" class="flex items-start text-sm gap-3">
            <span class="text-gray-400 whitespace-nowrap flex-shrink-0">{{ log.time }}</span>
            <i
              class="flex-shrink-0 mt-0.5"
              :class="{
                'fas fa-check-circle text-green-500': log.type === 'success',
                'fas fa-times-circle text-red-500': log.type === 'error',
                'fas fa-info-circle text-blue-500': log.type === 'info',
                'fas fa-exclamation-triangle text-yellow-500': log.type === 'warn',
              }"
            ></i>
            <span :class="log.type === 'error' ? 'text-red-700' : 'text-gray-700'">{{ log.message }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal détail photos -->
    <div v-if="detailItem" class="fixed inset-0 z-50 overflow-y-auto" @click.self="detailItem = null">
      <div class="flex items-center justify-center min-h-screen p-4">
        <div class="fixed inset-0 bg-gray-500 bg-opacity-60" @click="detailItem = null"></div>
        <div class="relative bg-white rounded-xl shadow-xl w-full max-w-2xl z-10">
          <div class="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <div>
              <h3 class="text-lg font-semibold text-gray-900">
                Photos — {{ detailItem.property.nsil || detailItem.property.propertyCode }}
              </h3>
              <p class="text-sm text-gray-500">{{ detailItem.photoStats.present }}/{{ detailItem.photoStats.total }} présentes</p>
            </div>
            <button @click="detailItem = null" class="text-gray-400 hover:text-gray-600">
              <i class="fas fa-times text-xl"></i>
            </button>
          </div>

          <div class="px-6 py-4 max-h-96 overflow-y-auto">
            <div v-if="detailItem.photos.length === 0" class="text-center py-8 text-gray-400">
              Aucune photo pour cette visite
            </div>
            <div v-else class="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <div
                v-for="(photo, idx) in detailItem.photos"
                :key="idx"
                class="relative rounded-lg border-2 overflow-hidden aspect-square flex items-center justify-center"
                :class="photo.existsLocally ? 'border-green-300 bg-green-50' : 'border-red-200 bg-red-50'"
              >
                <div class="absolute top-1 right-1">
                  <span
                    class="inline-flex items-center justify-center w-5 h-5 rounded-full text-xs"
                    :class="photo.existsLocally ? 'bg-green-500 text-white' : 'bg-red-400 text-white'"
                  >
                    <i :class="photo.existsLocally ? 'fas fa-check' : 'fas fa-times'"></i>
                  </span>
                </div>
                <div class="p-2 text-center">
                  <i
                    class="text-2xl mb-1 block"
                    :class="photo.existsLocally ? 'fas fa-image text-green-400' : 'fas fa-image text-red-300'"
                  ></i>
                  <p class="text-xs text-gray-600 break-all leading-tight">{{ photo.filename }}</p>
                  <p class="text-xs mt-1 font-medium" :class="photo.existsLocally ? 'text-green-600' : 'text-red-500'">
                    {{ photo.existsLocally ? 'Présente' : 'Manquante' }}
                  </p>
                  <p class="text-xs text-gray-400">{{ photo.source === 'visit' ? 'Visite' : 'Commentaire' }}</p>
                </div>
              </div>
            </div>
          </div>

          <div class="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
            <button @click="detailItem = null" class="px-4 py-2 border border-gray-300 text-sm rounded-md text-gray-700 hover:bg-gray-50">
              Fermer
            </button>
            <button
              v-if="detailItem.photoStats.missing > 0"
              @click="importVisit(detailItem); detailItem = null"
              :disabled="importingVisit[detailItem.visitId]"
              class="px-4 py-2 text-sm rounded-md text-white bg-purple-600 hover:bg-purple-700 disabled:opacity-50"
            >
              <i class="fas fa-cloud-download-alt mr-2"></i>
              Récupérer les {{ detailItem.photoStats.missing }} manquante(s)
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/services/api'

export default {
  name: 'PhotoImport',

  data() {
    const currentYear = new Date().getFullYear()
    const defaultYear = currentYear - 1

    return {
      selectedYear: defaultYear,
      visits: [],
      loading: false,
      importing: false,
      importingVisit: {},   // { [visitId]: true/false }
      importResults: {},    // { [visitId]: { success, message } }
      error: null,
      logs: [],
      detailItem: null,
    }
  },

  computed: {
    targetYear() {
      return this.selectedYear
    },

    availableYears() {
      const current = new Date().getFullYear()
      return [current - 1, current - 2, current - 3]
    },

    totalPhotos() {
      return this.visits.reduce((sum, v) => sum + v.photoStats.total, 0)
    },

    totalPresent() {
      return this.visits.reduce((sum, v) => sum + v.photoStats.present, 0)
    },

    totalMissing() {
      return this.visits.reduce((sum, v) => sum + v.photoStats.missing, 0)
    },
  },

  mounted() {
    this.loadData()
  },

  methods: {
    async loadData() {
      this.loading = true
      this.error = null
      this.importResults = {}

      try {
        const result = await api.getLastVisitsN1(this.selectedYear)
        this.visits = result.data || []
        this.addLog('info', `${this.visits.length} visite(s) chargée(s) pour ${this.selectedYear} — ${this.totalMissing} photo(s) manquante(s)`)
      } catch (err) {
        this.error = err.message || 'Erreur lors du chargement des données'
        this.addLog('error', `Erreur chargement : ${this.error}`)
      } finally {
        this.loading = false
      }
    },

    async importVisit(item) {
      // Vue 3 reactivity handles dynamic keys natively via Proxy
      this.importingVisit = { ...this.importingVisit, [item.visitId]: true }
      this.addLog('info', `Import des photos pour le lot ${item.property.nsil || item.property.propertyCode}…`)

      try {
        const result = await api.importVisitPhotos(item.visitId)

        this.importResults = {
          ...this.importResults,
          [item.visitId]: {
            success: result.success,
            message: result.message,
          }
        }

        const type = result.success ? 'success' : 'error'
        this.addLog(type, `Lot ${item.property.nsil || item.property.propertyCode} : ${result.message}`)

        if (result.errors && result.errors.length > 0) {
          result.errors.slice(0, 5).forEach(e => {
            this.addLog('warn', `  ↳ ${e.filename} : ${e.error}`)
          })
        }

        // Mettre à jour les compteurs localement
        if (result.copied > 0) {
          const visitIndex = this.visits.findIndex(v => v.visitId === item.visitId)
          if (visitIndex !== -1) {
            const v = this.visits[visitIndex]
            v.photoStats.present += result.copied
            v.photoStats.missing -= result.copied
            // Mettre à jour aussi les photos individuelles
            v.photos = v.photos.map(p => {
              if (!p.existsLocally) {
                const wasCopied = !result.errors?.find(e => e.filename === p.filename)
                return { ...p, existsLocally: wasCopied }
              }
              return p
            })
          }
        }
      } catch (err) {
        this.importResults = {
          ...this.importResults,
          [item.visitId]: { success: false, message: err.message || 'Erreur' }
        }
        this.addLog('error', `Erreur import lot ${item.property.nsil || item.property.propertyCode} : ${err.message}`)
      } finally {
        this.importingVisit = { ...this.importingVisit, [item.visitId]: false }
      }
    },

    async importAll() {
      if (!confirm(`Récupérer toutes les ${this.totalMissing} photos manquantes depuis le serveur distant ?\n\nCette opération peut prendre plusieurs minutes.`)) {
        return
      }

      this.importing = true
      this.addLog('info', `🚀 Import en masse démarré — ${this.totalMissing} photo(s) manquante(s)…`)

      try {
        const result = await api.importAllPhotos(this.selectedYear)

        const type = result.success ? 'success' : 'error'
        this.addLog(type, `Import terminé — ${result.message}`)

        if (result.errors && result.errors.length > 0) {
          this.addLog('warn', `${result.errors.length} erreur(s) :`)
          result.errors.slice(0, 10).forEach(e => {
            this.addLog('warn', `  ↳ ${e.filename} : ${e.error}`)
          })
        }

        // Recharger pour avoir les statuts à jour
        await this.loadData()
      } catch (err) {
        this.addLog('error', `Erreur import en masse : ${err.message}`)
      } finally {
        this.importing = false
      }
    },

    openDetail(item) {
      this.detailItem = item
    },

    progressWidth(photoStats) {
      if (photoStats.total === 0) return 0
      return Math.round((photoStats.present / photoStats.total) * 100)
    },

    formatDate(isoString) {
      if (!isoString) return '—'
      try {
        return new Date(isoString).toLocaleDateString('fr-FR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        })
      } catch {
        return isoString
      }
    },

    addLog(type, message) {
      this.logs.unshift({
        type,
        message,
        time: new Date().toLocaleTimeString('fr-FR'),
      })
      if (this.logs.length > 100) {
        this.logs = this.logs.slice(0, 100)
      }
    },
  },
}
</script>
